<template>
    <LoadingComponent :props="loading" />

    <div id="theme" class="db-card db-tab-div active" :class="{ 'on-update': onUpdateActive }">
        <div class="db-card-header items-center justify-between gap-4">
            <h3 class="db-card-title pt-1">{{ $t("menu.visual_theme") }}</h3>
            <div class="flex items-center gap-2">
                <button
                    type="button"
                    class="db-btn h-9 py-2 px-3 text-white bg-primary"
                    @click="refreshFromServer"
                    :disabled="isRefreshingView"
                    :class="{ 'disabled:opacity-50 disabled:bg-gray': isRefreshingView }"
                >
                    <template v-if="isRefreshingView">
                        <svg class="inline-block animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                            <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                            <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                        </svg>
                    </template>
                    <template v-else>
                        <i class="fa-solid fa-arrows-rotate"></i>
                    </template>
                </button>
            </div>
        </div>
        <div class="db-card-body">
            <form @submit.prevent="save" class="w-full d-block">
                <div class="form-row">
                    <!-- LOGOS -->
                    <div class="form-col-12 sm:form-col-4">
                        <label for="theme_logo" class="db-field-title">
                            {{ $t("label.logo") }} (128px,43px)
                        </label>
                        <input @change="changeLogo" v-bind:class="errors.theme_logo ? 'invalid' : ''" id="theme_logo"
                            type="file" class="db-field-control" ref="themeLogoProperty"
                            accept="image/png, image/jpeg, image/jpg, image/svg+xml, .svg" />
                        <small class="db-field-alert" v-if="errors.theme_logo">{{
                            errors.theme_logo[0]
                        }}</small>
                        <div class="w-full h-[120px] flex justify-center items-center rounded-lg border overflow-hidden p-4 mt-2">
                            <img class="w-auto h-auto h-[max-120px] p-6" alt="logo" v-if="theme_logo_reader"
                                :src="theme_logo_reader" />
                        </div>
                    </div>

                    <div class="form-col-12 sm:form-col-4">
                        <label for="fav_icon" class="db-field-title">
                            {{ $t("label.fav_icon") }} (120px,120px)
                        </label>
                        <input @change="changeFavIcon" v-bind:class="errors.theme_favicon_logo ? 'invalid' : ''"
                            id="fav_icon" type="file" class="db-field-control" ref="themeFaviconLogoProperty"
                            accept="image/png, image/jpeg, image/jpg, image/svg+xml, .svg" />
                        <small class="db-field-alert" v-if="errors.theme_favicon_logo">{{
                            errors.theme_favicon_logo[0]
                        }}</small>
                        <div class="w-full h-[120px] flex justify-center items-center rounded-lg border overflow-hidden p-4 mt-2">
                            <img class="w-auto h-auto h-[max-120px] p-6" alt="logo"
                                v-if="theme_favicon_logo_reader" :src="theme_favicon_logo_reader" />
                        </div>
                    </div>

                    <div class="form-col-12 sm:form-col-4">
                        <label for="footer_logo" class="db-field-title">
                            {{ $t("label.footer_logo") }} (144px,48px)
                        </label>
                        <input @change="changeFooterLogo" v-bind:class="errors.theme_footer_logo ? 'invalid' : ''"
                            id="footer_logo" type="file" class="db-field-control" ref="themeFooterLogoProperty"
                            accept="image/png, image/jpeg, image/jpg, image/svg+xml, .svg" />
                        <small class="db-field-alert" v-if="errors.theme_footer_logo">{{
                            errors.theme_footer_logo[0]
                        }}</small>
                        <div class="w-full h-[120px] flex justify-center items-center rounded-lg border bg-gray-200 overflow-hidden p-4 mt-2">
                            <img class="w-auto h-auto h-[max-120px] p-6" alt="logo"
                                v-if="theme_footer_logo_reader" :src="theme_footer_logo_reader" />
                        </div>
                    </div>

                    <!-- COLORES -->
                    <div class="form-col-12 border-t pt-5 mt-4">
                        <h4 class="text-base font-semibold text-gray-800 mb-2">Paleta de Colores</h4>
                        <p class="text-xs text-gray-500 mb-4">Define los colores primario y secundario. Las variantes Light y Slate se calculan de forma suave hacia la gama original.</p>
                    </div>

                    <!-- Color Primario -->
                    <div class="form-col-12 sm:form-col-6">
                        <label class="db-field-title">Color Primario (--primary)</label>
                        <div class="flex items-center gap-3">
                            <input type="color" v-model="form.theme_primary_color" class="h-10 w-14 p-1 rounded border cursor-pointer" />
                            <input type="text" v-model="form.theme_primary_color" class="db-field-control uppercase font-mono" placeholder="#426FFF" />
                        </div>
                        <small class="db-field-alert" v-if="errors.theme_primary_color">{{ errors.theme_primary_color[0] }}</small>
                        <!-- Muestra de Variantes -->
                        <div class="grid grid-cols-3 gap-2 mt-3 text-center text-xs">
                            <div class="p-3 rounded-md text-white font-medium shadow-sm transition-all" :style="{ backgroundColor: form.theme_primary_color }">
                                Base
                            </div>
                            <div class="p-3 rounded-md text-gray-800 font-medium shadow-sm transition-all" :style="{ backgroundColor: getLightColor(form.theme_primary_color) }">
                                Light
                            </div>
                            <div class="p-3 rounded-md text-gray-800 font-medium shadow-sm transition-all" :style="{ backgroundColor: getSlateColor(form.theme_primary_color) }">
                                Slate
                            </div>
                        </div>
                    </div>

                    <!-- Color Secundario -->
                    <div class="form-col-12 sm:form-col-6">
                        <label class="db-field-title">Color Secundario (--secondary)</label>
                        <div class="flex items-center gap-3">
                            <input type="color" v-model="form.theme_secondary_color" class="h-10 w-14 p-1 rounded border cursor-pointer" />
                            <input type="text" v-model="form.theme_secondary_color" class="db-field-control uppercase font-mono" placeholder="#79A9EC" />
                        </div>
                        <small class="db-field-alert" v-if="errors.theme_secondary_color">{{ errors.theme_secondary_color[0] }}</small>
                        <!-- Muestra de Variantes -->
                        <div class="grid grid-cols-3 gap-2 mt-3 text-center text-xs">
                            <div class="p-3 rounded-md text-white font-medium shadow-sm transition-all" :style="{ backgroundColor: form.theme_secondary_color }">
                                Base
                            </div>
                            <div class="p-3 rounded-md text-gray-800 font-medium shadow-sm transition-all" :style="{ backgroundColor: getLightColor(form.theme_secondary_color) }">
                                Light
                            </div>
                            <div class="p-3 rounded-md text-gray-800 font-medium shadow-sm transition-all" :style="{ backgroundColor: getSlateColor(form.theme_secondary_color) }">
                                Slate
                            </div>
                        </div>
                    </div>

                    <!-- FUENTES / GOOGLE FONTS -->
                    <div class="form-col-12 border-t pt-5 mt-4">
                        <h4 class="text-base font-semibold text-gray-800 mb-2">Tipografía & Fuentes (Google Fonts)</h4>
                        <p class="text-xs text-gray-500 mb-4">Selecciona las fuentes tipográficas para las distintas secciones de la aplicación.</p>
                    </div>

                    <!-- Cliente Font -->
                    <div class="form-col-12 sm:form-col-6 md:form-col-4 mb-3">
                        <label class="db-field-title">Cliente (--client-font)</label>
                        <vue-select class="db-field-control f-b-custom-select" id="theme_client_font"
                            v-bind:class="errors.theme_client_font ? 'invalid' : ''"
                            v-model="form.theme_client_font" :options="googleFonts" label-by="name" value-by="value"
                            :closeOnSelect="true" :searchable="true" :clearOnClose="true" placeholder="--"
                            search-placeholder="--" />
                        <small class="db-field-alert" v-if="errors.theme_client_font">{{ errors.theme_client_font[0] }}</small>
                    </div>

                    <!-- Admin Font -->
                    <div class="form-col-12 sm:form-col-6 md:form-col-4 mb-3">
                        <label class="db-field-title">Admin (--admin-font)</label>
                        <vue-select class="db-field-control f-b-custom-select" id="theme_admin_font"
                            v-bind:class="errors.theme_admin_font ? 'invalid' : ''"
                            v-model="form.theme_admin_font" :options="googleFonts" label-by="name" value-by="value"
                            :closeOnSelect="true" :searchable="true" :clearOnClose="true" placeholder="--"
                            search-placeholder="--" />
                        <small class="db-field-alert" v-if="errors.theme_admin_font">{{ errors.theme_admin_font[0] }}</small>
                    </div>

                    <!-- Title Font -->
                    <div class="form-col-12 sm:form-col-6 md:form-col-4 mb-3">
                        <label class="db-field-title">Título (--title-font)</label>
                        <vue-select class="db-field-control f-b-custom-select" id="theme_title_font"
                            v-bind:class="errors.theme_title_font ? 'invalid' : ''"
                            v-model="form.theme_title_font" :options="googleFonts" label-by="name" value-by="value"
                            :closeOnSelect="true" :searchable="true" :clearOnClose="true" placeholder="--"
                            search-placeholder="--" />
                        <small class="db-field-alert" v-if="errors.theme_title_font">{{ errors.theme_title_font[0] }}</small>
                    </div>

                    <!-- Table Font -->
                    <div class="form-col-12 sm:form-col-6 md:form-col-4 mb-3">
                        <label class="db-field-title">Tabla (--table-font)</label>
                        <vue-select class="db-field-control f-b-custom-select" id="theme_table_font"
                            v-bind:class="errors.theme_table_font ? 'invalid' : ''"
                            v-model="form.theme_table_font" :options="googleFonts" label-by="name" value-by="value"
                            :closeOnSelect="true" :searchable="true" :clearOnClose="true" placeholder="--"
                            search-placeholder="--" />
                        <small class="db-field-alert" v-if="errors.theme_table_font">{{ errors.theme_table_font[0] }}</small>
                    </div>

                    <!-- Mono Font -->
                    <div class="form-col-12 sm:form-col-6 md:form-col-4 mb-3">
                        <label class="db-field-title">Mono (--mono-font)</label>
                        <vue-select class="db-field-control f-b-custom-select" id="theme_mono_font"
                            v-bind:class="errors.theme_mono_font ? 'invalid' : ''"
                            v-model="form.theme_mono_font" :options="googleFonts" label-by="name" value-by="value"
                            :closeOnSelect="true" :searchable="true" :clearOnClose="true" placeholder="--"
                            search-placeholder="--" />
                        <small class="db-field-alert" v-if="errors.theme_mono_font">{{ errors.theme_mono_font[0] }}</small>
                    </div>

                    <!-- FOOTER BACKGROUND -->
                    <div class="form-col-12 border-t pt-5 mt-4">
                        <h4 class="text-base font-semibold text-gray-800 mb-2">Color de Fondo del Footer (Front)</h4>
                        <p class="text-xs text-gray-500 mb-4">Elige si el pie de página de la tienda pública utilizará el color primario o secundario.</p>
                    </div>

                    <div class="form-col-12 sm:form-col-6 mb-4">
                        <label class="db-field-title">Color de Fondo Footer</label>
                        <div class="flex items-center gap-6 mt-2">
                            <label class="flex items-center gap-2 cursor-pointer text-sm font-medium">
                                <input type="radio" value="primary" v-model="form.theme_footer_bg" class="w-4 h-4 text-primary" />
                                <span>Color Primario</span>
                            </label>
                            <label class="flex items-center gap-2 cursor-pointer text-sm font-medium">
                                <input type="radio" value="secondary" v-model="form.theme_footer_bg" class="w-4 h-4 text-primary" />
                                <span>Color Secundario</span>
                            </label>
                        </div>
                        <small class="db-field-alert" v-if="errors.theme_footer_bg">{{ errors.theme_footer_bg[0] }}</small>
                    </div>

                    <!-- CSS Y JS PERSONALIZADOS -->
                    <div class="form-col-12 border-t pt-5 mt-4">
                        <h4 class="text-base font-semibold text-gray-800 mb-2">CSS & JS Personalizado (Solo Frontend)</h4>
                        <p class="text-xs text-gray-500 mb-4">Inserta reglas CSS o scripts JavaScript adicionales. Solo se cargarán en el sitio público y en el área de cliente, sin afectar al panel de administración.</p>
                    </div>

                    <!-- Custom CSS -->
                    <div class="form-col-12 sm:form-col-6 mb-4">
                        <label class="db-field-title font-medium">CSS Personalizado</label>
                        <textarea v-model="form.theme_custom_css" rows="6" class="db-field-control font-mono text-xs leading-relaxed" placeholder="/* Escribe tu CSS personalizado aquí */&#10;body { }"></textarea>
                        <small class="db-field-alert" v-if="errors.theme_custom_css">{{ errors.theme_custom_css[0] }}</small>
                    </div>

                    <!-- Custom JS -->
                    <div class="form-col-12 sm:form-col-6 mb-4">
                        <label class="db-field-title font-medium">JS Personalizado</label>
                        <textarea v-model="form.theme_custom_js" rows="6" class="db-field-control font-mono text-xs leading-relaxed" placeholder="// Escribe tu JavaScript personalizado aquí&#10;console.log('Custom JS');"></textarea>
                        <small class="db-field-alert" v-if="errors.theme_custom_js">{{ errors.theme_custom_js[0] }}</small>
                    </div>

                    <!-- BOTÓN GUARDAR -->
                    <div class="form-col-12 border-t pt-5 mt-3">
                        <button type="submit" class="db-btn text-white bg-primary" :disabled="isSaving" :class="{ 'disabled:opacity-50 disabled:bg-gray': isSaving }">
                            <template v-if="isSaving">
                                <svg class="inline-block mr-1 animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                    <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                                </svg>
                                <span>{{ $t('button.updating') }}</span>
                            </template>
                            <template v-else>
                                <i class="lab lab-fill-save"></i>
                                <span>{{ $t("button.save") }}</span>
                            </template>
                        </button>
                    </div>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import LoadingComponent from "../../components/LoadingComponent";
import alertService from "../../../../services/alertService";

export default {
    name: "ThemeComponent",
    components: { LoadingComponent },
    data() {
        return {
            loading: {
                isActive: false,
            },
            theme_logo: "",
            theme_logo_reader: "",
            theme_favicon_logo: "",
            theme_favicon_logo_reader: "",
            theme_footer_logo: "",
            theme_footer_logo_reader: "",
            form: {
                theme_primary_color: "#426fff",
                theme_secondary_color: "#79a9ec",
                theme_client_font: "'Inter', sans-serif",
                theme_admin_font: "'IBM Plex Sans', sans-serif",
                theme_title_font: "'Google Sans', sans-serif",
                theme_table_font: "'IBM Plex Sans Condensed', sans-serif",
                theme_mono_font: "'IBM Plex Mono', 'Menlo', monospace",
                theme_footer_bg: "secondary",
                theme_custom_css: "",
                theme_custom_js: "",
            },
            googleFonts: [
                { name: "Inter", value: "'Inter', sans-serif" },
                { name: "Google Sans", value: "'Google Sans', sans-serif" },
                { name: "Bricolage Grotesque", value: "'Bricolage Grotesque', sans-serif" },
                { name: "IBM Plex Sans", value: "'IBM Plex Sans', sans-serif" },
                { name: "IBM Plex Sans Condensed", value: "'IBM Plex Sans Condensed', sans-serif" },
                { name: "IBM Plex Mono", value: "'IBM Plex Mono', 'Menlo', monospace" },
                { name: "Roboto", value: "'Roboto', sans-serif" },
                { name: "Open Sans", value: "'Open Sans', sans-serif" },
                { name: "Poppins", value: "'Poppins', sans-serif" },
                { name: "Montserrat", value: "'Montserrat', sans-serif" },
                { name: "Lato", value: "'Lato', sans-serif" },
                { name: "Oswald", value: "'Oswald', sans-serif" },
                { name: "Raleway", value: "'Raleway', sans-serif" },
                { name: "Nunito", value: "'Nunito', sans-serif" },
                { name: "Playfair Display", value: "'Playfair Display', serif" },
                { name: "Fira Code", value: "'Fira Code', monospace" },
                { name: "Source Code Pro", value: "'Source Code Pro', monospace" },
                { name: "Urbanist", value: "'Urbanist', sans-serif" },
                { name: "Plus Jakarta Sans", value: "'Plus Jakarta Sans', sans-serif" },
                { name: "Outfit", value: "'Outfit', sans-serif" },
                { name: "Space Grotesk", value: "'Space Grotesk', sans-serif" },
                { name: "DM Sans", value: "'DM Sans', sans-serif" },
                { name: "Manrope", value: "'Manrope', sans-serif" },
                { name: "Work Sans", value: "'Work Sans', sans-serif" },
                { name: "Syne", value: "'Syne', sans-serif" },
                { name: "Cabinet Grotesk", value: "'Cabinet Grotesk', sans-serif" },
            ],
            isSaving: false,
            isRefreshingView: false,
            onUpdateActive: false,
            errors: {},
        };
    },
    mounted() {
        this.list();
    },
    methods: {
        changeLogo: function (e) {
            this.theme_logo = e.target.files[0];
        },
        changeFavIcon: function (e) {
            this.theme_favicon_logo = e.target.files[0];
        },
        changeFooterLogo: function (e) {
            this.theme_footer_logo = e.target.files[0];
        },
        hexToRgb(hex) {
            if (!hex) return [66, 111, 255];
            hex = hex.toString().replace('#', '');
            if (hex.length === 3) {
                hex = hex.split('').map(c => c + c).join('');
            }
            if (hex.length === 6) {
                return [
                    parseInt(hex.substring(0, 2), 16),
                    parseInt(hex.substring(2, 4), 16),
                    parseInt(hex.substring(4, 6), 16)
                ];
            }
            return [66, 111, 255];
        },
        getLightColor(hex) {
            const rgb = this.hexToRgb(hex);
            const r = Math.min(255, Math.max(0, Math.round(rgb[0] + (255 - rgb[0]) * 0.35)));
            const g = Math.min(255, Math.max(0, Math.round(rgb[1] + (255 - rgb[1]) * 0.35)));
            const b = Math.min(255, Math.max(0, Math.round(rgb[2] + (255 - rgb[2]) * 0.35)));
            return `rgb(${r}, ${g}, ${b})`;
        },
        getSlateColor(hex) {
            const rgb = this.hexToRgb(hex);
            const r = Math.min(255, Math.max(0, Math.round(rgb[0] + (255 - rgb[0]) * 0.70)));
            const g = Math.min(255, Math.max(0, Math.round(rgb[1] + (255 - rgb[1]) * 0.70)));
            const b = Math.min(255, Math.max(0, Math.round(rgb[2] + (255 - rgb[2]) * 0.70)));
            return `rgb(${r}, ${g}, ${b})`;
        },
        list: function (showOverlay = true) {
            if (showOverlay) {
                this.loading.isActive = true;
            }
            return this.$store
                .dispatch("theme/lists")
                .then((res) => {
                    const data = res.data.data;
                    this.theme_logo_reader = data.theme_logo;
                    this.theme_favicon_logo_reader = data.theme_favicon_logo;
                    this.theme_footer_logo_reader = data.theme_footer_logo;
                    this.form.theme_primary_color = data.theme_primary_color || "#426fff";
                    this.form.theme_secondary_color = data.theme_secondary_color || "#79a9ec";
                    this.form.theme_client_font = data.theme_client_font || "'Inter', sans-serif";
                    this.form.theme_admin_font = data.theme_admin_font || "'IBM Plex Sans', sans-serif";
                    this.form.theme_title_font = data.theme_title_font || "'Google Sans', sans-serif";
                    this.form.theme_table_font = data.theme_table_font || "'IBM Plex Sans Condensed', sans-serif";
                    this.form.theme_mono_font = data.theme_mono_font || "'IBM Plex Mono', 'Menlo', monospace";
                    this.form.theme_footer_bg = data.theme_footer_bg || "secondary";
                    this.form.theme_custom_css = data.theme_custom_css || "";
                    this.form.theme_custom_js = data.theme_custom_js || "";

                    if (showOverlay) {
                        this.loading.isActive = false;
                    }
                })
                .catch((err) => {
                    if (showOverlay) {
                        this.loading.isActive = false;
                    }
                });
        },
        async refreshFromServer() {
            if (this.isRefreshingView) {
                return;
            }
            this.isRefreshingView = true;
            this.onUpdateActive = true;
            try {
                await this.list(false);
                this.theme_logo = "";
                this.theme_favicon_logo = "";
                this.theme_footer_logo = "";
                this.errors = {};
                if (this.$refs.themeLogoProperty) this.$refs.themeLogoProperty.value = null;
                if (this.$refs.themeFaviconLogoProperty) this.$refs.themeFaviconLogoProperty.value = null;
                if (this.$refs.themeFooterLogoProperty) this.$refs.themeFooterLogoProperty.value = null;
                alertService.info(this.$t('button.refresh'));
            } finally {
                this.isRefreshingView = false;
                this.onUpdateActive = false;
            }
        },
        save: function () {
            try {
                this.isSaving = true;
                this.onUpdateActive = true;
                const fd = new FormData();
                if (this.theme_logo) {
                    fd.append("theme_logo", this.theme_logo);
                }
                if (this.theme_favicon_logo) {
                    fd.append("theme_favicon_logo", this.theme_favicon_logo);
                }
                if (this.theme_footer_logo) {
                    fd.append("theme_footer_logo", this.theme_footer_logo);
                }
                fd.append("theme_primary_color", this.form.theme_primary_color);
                fd.append("theme_secondary_color", this.form.theme_secondary_color);
                fd.append("theme_client_font", this.form.theme_client_font);
                fd.append("theme_admin_font", this.form.theme_admin_font);
                fd.append("theme_title_font", this.form.theme_title_font);
                fd.append("theme_table_font", this.form.theme_table_font);
                fd.append("theme_mono_font", this.form.theme_mono_font);
                fd.append("theme_footer_bg", this.form.theme_footer_bg);
                fd.append("theme_custom_css", this.form.theme_custom_css || "");
                fd.append("theme_custom_js", this.form.theme_custom_js || "");

                this.$store
                    .dispatch("theme/save", {
                        form: fd,
                    })
                    .then((res) => {
                        alertService.successFlip(1, this.$t("menu.theme"));
                        this.list(false);
                        this.theme_logo = "";
                        this.theme_favicon_logo = "";
                        this.theme_footer_logo = "";
                        this.errors = {};
                        if (this.$refs.themeLogoProperty) this.$refs.themeLogoProperty.value = null;
                        if (this.$refs.themeFaviconLogoProperty) this.$refs.themeFaviconLogoProperty.value = null;
                        if (this.$refs.themeFooterLogoProperty) this.$refs.themeFooterLogoProperty.value = null;
                        this.isSaving = false;
                        this.onUpdateActive = false;
                        // Trigger reload of global settings so changes apply immediately
                        this.$store.dispatch('frontendSetting/lists').then().catch();
                    })
                    .catch((err) => {
                        if (err.response && err.response.data && err.response.data.errors) {
                            this.errors = err.response.data.errors;
                        }
                        this.isSaving = false;
                        this.onUpdateActive = false;
                    });
            } catch (err) {
                this.isSaving = false;
                this.onUpdateActive = false;
                alertService.error(err);
            }
        },
    },
};
</script>
